<script setup lang="ts">
import { refDebounced, useFocus, watchDebounced } from '@vueuse/core';

import { computed, ref, watch, type Ref } from 'vue';

const props = defineProps<{
  items: Ref<string[]>;
  selected: Ref<string[]>;
  placeholder: string;
  onEnter: (search: string) => void;
}>();

const emit = defineEmits<{
  (e: 'update:optionSelect', option: string): void;
  (e: 'update:searchQueryChanged', query: string): void;
  (e: 'inputLostFocus'): void;
}>();

const searchQuery = ref('');
const selectedIdx = ref(0);
const inputElement = ref<HTMLInputElement | null>(null);
const { focused } = useFocus(inputElement);
const isOpen = refDebounced(focused, 100);
const preventBlur = ref(false);

const filteredOptions = computed(() => {
  return props.items.value.filter((option) =>
    option.toLowerCase().includes(searchQuery.value.toLowerCase())
  );
});

const autocompleteSuffix = computed(() => {
  if (
    !focused.value ||
    !searchQuery.value ||
    filteredOptions.value.length === 0 ||
    selectedIdx.value < 0 ||
    selectedIdx.value >= filteredOptions.value.length
  ) {
    return '';
  }
  const selectedOption = filteredOptions.value[selectedIdx.value];
  if (selectedOption.toLowerCase().startsWith(searchQuery.value.toLowerCase())) {
    return selectedOption.substring(searchQuery.value.length);
  }

  return '';
});

function selectOption(option: string, clicked: boolean = false) {
  preventBlur.value = true;
  searchQuery.value = '';
  selectedIdx.value = 0;
  emit('update:optionSelect', option);
  setTimeout(
    () => {
      focused.value = false;
      preventBlur.value = false;
    },
    clicked ? 25 : 0
  );
}

function handleClickEnter() {
  if (filteredOptions.value.length > 0) {
    selectOption(filteredOptions.value[selectedIdx.value]);
  }
}

function handleBlur() {
  if (!preventBlur.value) {
    emit('inputLostFocus');
  }
  preventBlur.value = false;
}

function select(dir: 'next' | 'prev') {
  if (!filteredOptions.value.length) return;

  if (dir === 'next') {
    selectedIdx.value = (selectedIdx.value + 1) % filteredOptions.value.length;
  } else {
    selectedIdx.value =
      selectedIdx.value - 1 < 0 ? filteredOptions.value.length - 1 : selectedIdx.value - 1;
  }
}

function handleKeyDown(e: KeyboardEvent) {
  if (!filteredOptions.value.length) return;

  switch (e.key) {
    case 'ArrowDown':
      e.preventDefault();
      select('next');
      break;
    case 'ArrowUp':
      e.preventDefault();
      select('prev');
      break;
    case 'Tab':
      e.preventDefault();
      select(e.shiftKey ? 'prev' : 'next');
      break;
  }
}

watchDebounced(
  () => searchQuery.value,
  (query) => {
    selectedIdx.value = 0;
    emit('update:searchQueryChanged', query);
  },
  { debounce: 125, maxWait: 250 }
);

watch(
  () => filteredOptions.value.length,
  () => {
    selectedIdx.value = 0;
  }
);

defineExpose({
  filteredOptions,
  selectedIdx,
  selectOption
});
</script>

<template>
  <div class="relative flex w-full flex-col" ref="container">
    <div class="relative flex flex-row items-center justify-end">
      <!-- Autocomplete-ish -->
      <span
        v-if="autocompleteSuffix"
        class="pointer-events-none absolute left-0 top-0 z-10 flex h-10 w-full items-center overflow-hidden whitespace-pre p-3 text-secondary-accent/50"
        aria-hidden="true"
        style="pointer-events: none"
      >
        <span class="invisible">{{ searchQuery }}</span
        >{{ autocompleteSuffix }}
      </span>
      <input
        ref="inputElement"
        v-model="searchQuery"
        @input="true"
        @click.prevent="focused = true"
        class="relative flex h-10 w-full appearance-none gap-3 truncate rounded border border-primary bg-neutral p-3 text-secondary-accent shadow-light-border ring-offset-primary placeholder:text-secondary-accent focus:border-accent focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring focus-visible:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-50"
        type="text"
        :placeholder="props.placeholder"
        @blur="handleBlur"
        @keyup.enter.prevent="handleClickEnter"
        @keydown="handleKeyDown"
        :title="searchQuery"
      />
    </div>
    <div
      v-show="isOpen"
      class="absolute bottom-full max-h-[150px] min-h-12 w-full overflow-y-auto overflow-x-hidden rounded shadow-lg"
      @mousedown="preventBlur = true"
    >
      <ul
        v-if="filteredOptions.length > 0"
        class="grid grid-flow-dense grid-rows-1 overflow-auto py-1"
      >
        <li
          v-for="(option, index) in filteredOptions"
          :key="index"
          class="block w-full cursor-pointer gap-2 truncate whitespace-nowrap rounded border-2 border-primary bg-neutral px-4 py-2 hover:bg-primary hover:text-accent"
          :title="option"
          :class="{
            'border-warning bg-warning-content text-primary-content hover:bg-warning-content hover:text-primary-content/80':
              index === selectedIdx
          }"
          @click.prevent="selectOption(option, true)"
          @mouseover="selectedIdx = index"
        >
          {{ option }}
        </li>
      </ul>
      <div
        class="flex items-center gap-2 rounded border-2 border-primary/50 bg-neutral px-4 py-2 text-primary"
        v-else-if="searchQuery"
      >
        Tag not found
      </div>
    </div>
  </div>
</template>
